// // src/pages/avsync/ScannerDocs.js
// // AV Sync — Local Scanner Utility Documentation
// import React from "react";
// import "../../App.css";

// export default function ScannerDocs() {
//   return (
//     <div className="posts">
//       <h1>AV Sync — Local Scanner Utility</h1>
//       <p className="intro-text">
//         Instructions for downloading, running, and connecting the standalone
//         Local Scanner Utility to your AV Sync account.
//       </p>

//       {/* WHAT IT DOES */}
//       <div className="post-item">
//         <h2 className="post-title">What the Scanner Utility Does</h2>
//         <p className="post-pargrphs">
//           Because browsers cannot scan local networks for security reasons, AV
//           Sync uses a separate Node-based tool that:
//         </p>
//         <ul className="post-pargrphs">
//           <li>Scans your local network (IP + MAC)</li>
//           <li>Generates device entries</li>
//           <li>Sends them securely to your AV Sync account using your UID</li>
//         </ul>
//       </div>

//       {/* DOWNLOAD */}
//       <div className="post-item">
//         <h2 className="post-title">Download</h2>
//         <a
//           href="/assets/AVSync-LocalScanner-Release.zip"
//           download
//           className="download-btn"
//         >
//           Download Local Scanner Utility
//         </a>
//       </div>

//       {/* HOW TO RUN */}
//       <div className="post-item">
//         <h2 className="post-title">How to Run (macOS + Windows)</h2>

//         <h3>macOS</h3>
//         <ol className="post-pargrphs">
//           <li>Unzip the folder</li>
//           <li>Open Terminal</li>
//           <li>Drag the folder into Terminal (auto-fills the path)</li>
//           <li>
//             Run: <code>sh run-scanner.sh</code>
//           </li>
//           <li>Enter your UID from AV Sync → Settings</li>
//         </ol>

//         <h3>Windows</h3>
//         <ol className="post-pargrphs">
//           <li>Unzip the folder</li>
//           <li>
//             Double-click <code>run-scanner.bat</code>
//           </li>
//           <li>Paste your Firebase UID</li>
//         </ol>
//       </div>

//       {/* NEXT SECTION */}
//       <div className="post-item">
//         <h2 className="post-title">Related Documentation</h2>
//         <ul className="post-pargrphs">
//           <li>
//             <a className="post-links" href="/avsync/handoff">
//               Project Handoff Documentation
//             </a>
//           </li>
//           <li>
//             <a className="post-links" href="/avsync">
//               Back to Main AV Sync Page
//             </a>
//           </li>
//         </ul>
//       </div>
//     </div>
//   );
// }
import React from "react";
import "../../App.css";

export default function ScannerDocs() {
  return (
    <div className="posts">
      <h1>AV Sync — Local Scanner Utility Documentation</h1>
      <p className="intro-text">
        The Local Scanner Utility allows AV Sync users to run real device
        discovery on their own network. This standalone Node.js tool scans for
        IP + MAC addresses and securely uploads them to the user’s AV Sync
        account.
      </p>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">📌 Why a Local Scanner Utility?</h2>
        <p className="post-pargrphs">
          Web browsers cannot scan a user’s local network for security reasons.
          Modern browsers are sandboxed, meaning they cannot access:
        </p>

        <ul className="post-pargrphs">
          <li>Local IP ranges (192.168.x.x, 10.x.x.x)</li>
          <li>MAC addresses</li>
          <li>Subnet information</li>
          <li>Network discovery protocols</li>
        </ul>

        <p className="post-pargrphs">
          The Local Scanner Utility solves this by running natively on the
          user’s machine. It scans the local LAN and sends results to the AV
          Sync backend using HTTPS.
        </p>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">Architecture Overview</h2>

        <pre className="code-block">{`
[ Your Computer ] ---runs---> [ Local Scanner Utility ]
          │
          │  Discovers devices via local-devices
          ▼
[ Scanner sends payload --> HTTPS ] 
          │
          ▼
[ AV Sync Backend API ] 
          │
          ▼
[ MongoDB — devices saved under user's UID ]
        `}</pre>

        <p className="post-pargrphs">
          The utility always associates each device with your unique Firebase
          UID. (DeviceID+UID = ID)
        </p>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">Understanding Your UID</h2>

        <p className="post-pargrphs">
          The UID is your unique Firebase Authentication ID. Every user in AV
          Sync has a UID that identifies which devices belong to them.
        </p>

        <p className="post-pargrphs">
          You can find your UID inside the AV Sync Web App under:
          <strong> Settings → User Information → UID </strong>
        </p>

        <p className="post-pargrphs">
          When running the scanner, you will be prompted to paste your UID.
        </p>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">📥 Download</h2>
        <a
          className="download-btn"
          href="/assets/AVSync-LocalScanner-Release.zip"
          download
        >
          Download Scanner Utility
        </a>
        <p className="post-pargrphs">
          You can also download the latest signed release directly from GitHub:{" "}
          <a
            className="post-links"
            href="https://github.com/VictoriaSavannah-FS/avsync-scanner-public/releases/latest"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Latest Release →
          </a>
        </p>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">Step-by-Step Instructions (macOS)</h2>

        <ol className="post-pargrphs">
          <li>
            Download and unzip <strong>AVSync-LocalScanner-Release.zip</strong>
          </li>
          <li>Open Terminal</li>
          <li>
            Type <code>cd </code> and add a space
          </li>
          <li>
            Drag the unzipped folder into Terminal (auto-completes the path)
          </li>
          <li>Press Enter</li>
          <li>
            Run the scanner:
            <pre className="code-block">sh run-scanner.sh</pre>
          </li>
          <li>When prompted, paste your UID</li>
          <li>Press Enter to begin the scan</li>
        </ol>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">Windows Instructions</h2>

        <ol className="post-pargrphs">
          <li>Unzip the release folder</li>
          <li>Open Command Prompt</li>
          <li>
            Navigate to the folder:
            <pre className="code-block">
              cd path\to\AVSync-LocalScanner-Release
            </pre>
          </li>
          <li>
            Run:
            <pre className="code-block">run-scanner.bat</pre>
          </li>
          <li>Paste your UID when prompted</li>
        </ol>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">Sample Output</h2>

        <pre className="code-block">{`
🔍 Starting AV Sync Local Network Scan...

📌 Using UID: abc123XYZ

🔎 Found 9 device(s)

✅ Saved: 192.168.1.10 (HP-Printer)
⚠️ Skipped duplicate: 192.168.1.41
📡 Sent → Apple-TV (192.168.1.41)
📡 Sent → Unnamed Device: 8

🎉 Scan Complete!
        `}</pre>
      </div>

      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">🔐 Security Notes</h2>

        <ul className="post-pargrphs">
          <li>No ports are opened on your network</li>
          <li>No device configuration data is accessed</li>
          <li>Only IP + MAC address discovery</li>
          <li>All data is sent over HTTPS</li>
        </ul>
      </div>
      {/* ------------------------------------------------------- */}
      <div className="post-item">
        <h2 className="post-title">License</h2>
        <p className="post-pargrphs">
          The AV Sync Local Scanner Utility is open-source and licensed under
          the{" "}
          <a
            className="post-links"
            href="https://github.com/VictoriaSavannah-FS/avsync-scanner-public/blob/main/LICENSE"
            target="_blank"
            rel="noopener noreferrer"
          >
            MIT License
          </a>
          . You are free to use, modify, and extend this tool in your own
          projects.
        </p>
      </div>
    </div>
  );
}
